/**
 * RetryableRequest — timeout, retry, and bench logging for Gateway requests
 *
 * Wraps a RequestHandler-like object, adding:
 * - Per-command timeout (aborts the internal fetch if exceeded)
 * - Automatic retries with exponential backoff for transient errors
 * - [bench] logging for every attempt (success, timeout, or error)
 *
 * Non-retryable errors (auth, validation, rate-limit, caller abort,
 * DNS/connection TypeError) are rethrown immediately.
 */

import type { ApiResponse } from './types';
import { GatewayError, GatewayErrorCode } from './error-codes';
import { logger } from '../logger';
import {
  GATEWAY_TIMEOUTS,
  GATEWAY_TIMEOUT_DEFAULT,
  GATEWAY_RETRIES,
  GATEWAY_RETRY_DEFAULT,
  GATEWAY_RETRY_BACKOFF_BASE_MS,
  GATEWAY_RETRY_BACKOFF_MULTIPLIER,
  GATEWAY_NO_TIMEOUT_RETRY,
} from '../app-constants';

// ── Public types ────────────────────────────────────────────────────────

/** Callback invoked before each retry sleep. */
export type RetryCallback = (
  attempt: number,
  maxAttempts: number,
  command: string
) => void;

/** Per-call overrides (useful for tests and per-command tuning). */
export interface RetryOverrides {
  timeoutMs?: number;
  maxRetries?: number;
  backoffBaseMs?: number;
  backoffMultiplier?: number;
  onRetry?: RetryCallback;
}

/** Minimal interface the handler must satisfy. */
export interface RequestHandlerLike {
  makeRequest(
    command: string,
    language: string,
    data: string,
    signal?: AbortSignal
  ): Promise<ApiResponse>;
}

// ── Helpers ─────────────────────────────────────────────────────────────

const UPSTREAM_TIMEOUT_THRESHOLD_MS = 55_000;

/**
 * Returns true if the error is transient and the request should be retried.
 * A 502/503/504 that took >55s is an upstream timeout at some hop (the gateway
 * answers 503 when relations times out, the proxy 502): deterministic, so
 * retrying the same content won't help. Transient 5xx (a restarting pod) come
 * back fast.
 */
function isRetryable(
  error: unknown,
  command: string,
  elapsedMs?: number
): boolean {
  if (error instanceof TypeError) return false;

  if (error instanceof GatewayError) {
    const nonRetryableCodes: string[] = [
      GatewayErrorCode.AUTH_REQUIRED,
      GatewayErrorCode.CSRF_MISSING,
      GatewayErrorCode.RATE_LIMITED,
      GatewayErrorCode.SESSION_EXPIRED,
      GatewayErrorCode.SESSION_ENDED,
      GatewayErrorCode.CSRF_REJECTED,
      GatewayErrorCode.FORBIDDEN,
    ];
    if (nonRetryableCodes.includes(error.code)) return false;

    const status = error.details?.status;
    if (typeof status === 'number') {
      const nonRetryableStatuses = [401, 403, 409, 422, 429];
      if (nonRetryableStatuses.includes(status)) return false;
      if ([502, 503, 504].includes(status)) {
        if (
          elapsedMs !== undefined &&
          elapsedMs >= UPSTREAM_TIMEOUT_THRESHOLD_MS
        ) {
          logger.info(
            `[retry] ${status} after ${elapsedMs}ms — upstream timeout, skipping retries`
          );
          return false;
        }
        return true;
      }
    }

    if (error.code === GatewayErrorCode.TIMEOUT) {
      return !GATEWAY_NO_TIMEOUT_RETRY.includes(command);
    }
  }

  return false;
}

/**
 * Try to extract the length of a `text` field from a JSON data string.
 */
function extractTextLength(data: string): number | null {
  try {
    const parsed = JSON.parse(data);
    if (typeof parsed.text === 'string') return parsed.text.length;
  } catch {
    // not JSON — that's fine
  }
  return null;
}

/**
 * Derive a short status token for the bench log line.
 */
function extractStatus(error: unknown): string {
  if (
    error instanceof GatewayError &&
    error.code === GatewayErrorCode.TIMEOUT
  ) {
    return 'timeout';
  }
  if (error instanceof GatewayError) {
    const status = error.details?.status;
    if (typeof status === 'number') return String(status);
    return error.code;
  }
  if (error instanceof TypeError) return 'network_error';
  return 'error';
}

// ── Class ───────────────────────────────────────────────────────────────

export class RetryableRequest {
  private handler: RequestHandlerLike;

  constructor(handler: RequestHandlerLike) {
    this.handler = handler;
  }

  /**
   * Execute a Gateway command with timeout, retry, and bench logging.
   */
  async execute(
    command: string,
    language: string,
    data: string,
    callerSignal?: AbortSignal,
    overrides?: RetryOverrides
  ): Promise<ApiResponse> {
    const timeoutMs =
      overrides?.timeoutMs ??
      GATEWAY_TIMEOUTS[command] ??
      GATEWAY_TIMEOUT_DEFAULT;
    const maxRetries =
      overrides?.maxRetries ??
      GATEWAY_RETRIES[command] ??
      GATEWAY_RETRY_DEFAULT;
    const backoffBaseMs =
      overrides?.backoffBaseMs ?? GATEWAY_RETRY_BACKOFF_BASE_MS;
    const backoffMultiplier =
      overrides?.backoffMultiplier ?? GATEWAY_RETRY_BACKOFF_MULTIPLIER;

    const totalAttempts = maxRetries + 1;
    const charsCount = extractTextLength(data);
    let lastError: unknown;

    for (let attempt = 0; attempt < totalAttempts; attempt++) {
      const start = performance.now();

      // Create a per-attempt AbortController with a timeout
      const internalController = new AbortController();
      const timer = setTimeout(() => internalController.abort(), timeoutMs);

      // Link caller's abort signal to the internal controller
      const onCallerAbort = () => internalController.abort();
      if (callerSignal) {
        if (callerSignal.aborted) {
          clearTimeout(timer);
          throw (
            callerSignal.reason ??
            new DOMException('The operation was aborted.', 'AbortError')
          );
        }
        callerSignal.addEventListener('abort', onCallerAbort, { once: true });
      }

      try {
        const result = await this.handler.makeRequest(
          command,
          language,
          data,
          internalController.signal
        );

        clearTimeout(timer);
        if (callerSignal) {
          callerSignal.removeEventListener('abort', onCallerAbort);
        }

        // Success bench line
        const elapsed = Math.round(performance.now() - start);
        this.logBench(
          command,
          charsCount,
          '200',
          attempt + 1,
          totalAttempts,
          elapsed
        );

        return result;
      } catch (error) {
        clearTimeout(timer);
        if (callerSignal) {
          callerSignal.removeEventListener('abort', onCallerAbort);
        }

        // Determine if this was a timeout from our internal controller
        const isAbortError =
          (error instanceof DOMException && error.name === 'AbortError') ||
          (error instanceof Error && error.name === 'AbortError');

        // Check if it's a caller abort (not our timeout)
        if (isAbortError && callerSignal?.aborted) {
          const elapsed = Math.round(performance.now() - start);
          this.logBench(
            command,
            charsCount,
            'aborted',
            attempt + 1,
            totalAttempts,
            elapsed
          );
          throw error;
        }

        const elapsed = Math.round(performance.now() - start);

        // Our own timeout — wrap as GatewayError
        if (isAbortError) {
          lastError = new GatewayError(
            `Request timed out after ${timeoutMs}ms for command '${command}'`,
            GatewayErrorCode.TIMEOUT,
            { timeoutMs, attempt: attempt + 1 }
          );
          this.logBench(
            command,
            charsCount,
            'timeout',
            attempt + 1,
            totalAttempts,
            elapsed
          );
        } else {
          lastError = error;
          const status = extractStatus(error);
          this.logBench(
            command,
            charsCount,
            status,
            attempt + 1,
            totalAttempts,
            elapsed
          );
        }

        // Check retryability — pass elapsed so slow upstream-timeout 5xx are skipped
        if (!isRetryable(lastError, command, elapsed)) {
          throw lastError;
        }

        // If there are retries left, sleep with backoff
        if (attempt < totalAttempts - 1) {
          overrides?.onRetry?.(attempt + 2, totalAttempts, command);
          const delay = backoffBaseMs * Math.pow(backoffMultiplier, attempt);
          await this.sleep(delay, callerSignal);
        }
      }
    }

    // All attempts exhausted
    throw lastError;
  }

  /**
   * Emit a [bench] log line.
   */
  private logBench(
    command: string,
    chars: number | null,
    status: string,
    attempt: number,
    totalAttempts: number,
    elapsedMs: number
  ): void {
    const parts = [`[bench] command=${command}`];
    if (chars !== null) parts.push(`chars=${chars}`);
    parts.push(`status=${status}`);
    parts.push(`attempt=${attempt}/${totalAttempts}`);
    parts.push(`elapsed_ms=${elapsedMs}`);
    logger.info(parts.join(' '));
  }

  /**
   * Sleep that can be interrupted by a caller AbortSignal.
   */
  private sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(
          signal.reason ??
            new DOMException('The operation was aborted.', 'AbortError')
        );
        return;
      }
      const timer = setTimeout(resolve, ms);
      if (signal) {
        const onAbort = () => {
          clearTimeout(timer);
          reject(
            signal.reason ??
              new DOMException('The operation was aborted.', 'AbortError')
          );
        };
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }
}
